import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  getGuidedViewPage,
  getMarketDenomination,
  getSessionInitDataState,
} from '../../store/sessionDataSlice'
import { getLatestConfigChangeResult } from '../../store/truckInfoSlice'
import { ActiveChoicesIcon } from '../../pages/GuidedView/ActiveChoicesIcon'
import { GuidedViewPageType } from '../../store/types'
import { closeGvPanel } from '../../store/sidePanelSlice'
import { useClient } from '../../utils/useClient'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingClickActiveChangesEvent,
} from '../../utils/adobeAnalytics'
import { buildRelativeAllViewPage } from '../../utils/UrlBuilders'
import { getUrlParametersToString } from '../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'
import { getMarketLanguageState } from '../../store/appSlice'
import { setMenuIdToOpenAtJumpToAllView } from '../../store/menuSlice'

const ContentRoot = styled.div``

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
`

const Header = styled.h2`
  margin-left: 16px;
`

const Text = styled.p`
  margin-bottom: 24px;
`

const ActiveChangesCard = styled.div`
  border: 1px solid var(--tds-grey-300);
  border-radius: 6px;
  padding: 8px;
  padding-left: 24px;
  margin-bottom: 16px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--tds-grey-50);
      cursor: pointer;
    }
  }
`

const CardHeader = styled.div`
  color: var(--tds-grey-600);
`

const CardText = styled.div`
  font-weight: bold;
`

const ActiveChangesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`

export interface ContentActiveChangesProps {
  navigateToMenu: (
    id: string,
    menuPath: string | null | undefined,
    gvPageToJumpTo: GuidedViewPageType,
  ) => void
  menuPath?: string
}

export function ContentActiveChanges({
  navigateToMenu,
  menuPath,
}: ContentActiveChangesProps): JSX.Element {
  const client = useClient()
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const navigate = useNavigate()
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const marketDenomination = useAppSelector(getMarketDenomination)
  const latestConfigChangeResult = useAppSelector(getLatestConfigChangeResult)
  const activeChoicesState = latestConfigChangeResult?.activeChoices
  const guidedViewPage = useAppSelector(getGuidedViewPage)
  const sessionInitData = useAppSelector(getSessionInitDataState)

  const handleActiveChangesItemClick = (
    id: string,
    menuPath: string | null | undefined,
  ) => {
    dispatch(closeGvPanel())
    const trackingEvent: ScaniaAdobeTrackingClickActiveChangesEvent = {
      event: ScaniaAdobeEventId.ActiveChangesClick,
    }
    pushAdobeEvent(trackingEvent)

    if (guidedViewPage === GuidedViewPageType.allView) {
      navigateToMenu(id, menuPath, GuidedViewPageType.allView)
      return
    }

    if (guidedViewPage === GuidedViewPageType.guidedView) {
      if (!sessionInitData) {
        throw new Error('Expected sessionInitData to be defined.')
      }
      if (!client) {
        throw new Error('Expeted client to be defined.')
      }
      const asyncWrapper = async () => {
        const exploreActvieItemResult = await client.exploreActiveitem({
          sessionId: sessionInitData.sessionId,
          menuId: id,
        })
        if (exploreActvieItemResult.success) {
          const itemAvailableInGv =
            exploreActvieItemResult.success.idIsPresentInGvMenu
          const menuPathGv = exploreActvieItemResult.success.menuPathGv
          if (itemAvailableInGv) {
            navigateToMenu(id, menuPathGv, GuidedViewPageType.guidedView)
          } else {
            if (!marketLanguage) {
              throw new Error('Expected market language.')
            }

            const relativeUrl = buildRelativeAllViewPage(marketLanguage)
            const params = relativeUrl.params
            const newUrl = relativeUrl.path + getUrlParametersToString(params)
            navigate(newUrl)
            const etelFamily = 'SUBMENU_' + id.split('~')[0]
            dispatch(setMenuIdToOpenAtJumpToAllView(etelFamily))
          }
        }
      }
      asyncWrapper()
    }
  }

  return (
    <ContentRoot>
      <HeaderWrapper>
        <ActiveChangesWrapper>
          <ActiveChoicesIcon
            choiceCount={activeChoicesState?.length || 0}
            isHighlighted={false}
          />
        </ActiveChangesWrapper>
        <Header>{marketDenomination}</Header>
      </HeaderWrapper>
      <Text>{t2(TextId.SIDEPANEL_DESCRIPTION_ACTIVE_CHANGES)}</Text>
      {activeChoicesState &&
        activeChoicesState.map((item, i) => {
          return (
            <ActiveChangesCard
              key={'ActiveChoices' + item.itemId + '-' + i}
              onClick={() =>
                handleActiveChangesItemClick(item.itemId, item.menuPath)
              }
            >
              <CardHeader>{item.menuText}:</CardHeader>
              <CardText>{item.itemText}</CardText>
            </ActiveChangesCard>
          )
        })}
    </ContentRoot>
  )
}
